import { LowerCasePipe, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { connectState } from '@examdojo/angular/util';
import { getAnimationSlideInToLeft } from '@examdojo/animation';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import {
  MarkGroupFormGroup,
  MarkschemeEditorItemEnum,
  MarkschemeImageBucketDirective,
  MarkschemeMarkgroupEditorComponent,
} from '@examdojo/markscheme-v2';
import { ContentItemTypeEnum, ImageContentItemFormGroup } from '@examdojo/models/content-item';
import { QuestionItemTypeEnum } from '@examdojo/models/question';
import {
  ContentItemEditorComponent,
  ContentItemFormArray,
  ContentItemFormGroup,
  QuestionTableEditorComponent,
  TableContentItemFormGroup,
} from '@examdojo/question-content-item';
import { QuestionImageEditorComponent } from '@examdojo/question-image';
import { QuestionValidationHeaderComponent } from '../cms';
import { QuestionItemOptionsEditorComponent } from './options/question-item-options-editor.component';
import {
  ActiveQuestionEditorItem,
  ActiveQuestionEditorSubItemCoordinates,
  CONTENT_ITEM_TYPE_TO_LONG_LABEL,
  StemFormGroup,
  StemGroupFormGroup,
  StimulusFormGroup,
} from './question-item-editor.model';
import { QuestionItemsFormService } from './question-items-form.service';

@Component({
  selector: 'dojo-question-active-item-editor',
  template: `
    @if (state.editingOptions) {
      @if (activeQuestionSubItem(); as subItem) {
        <div class="absolute left-0 right-0 top-0 z-[10] flex h-full w-full flex-col bg-white" @slideInToLeft>
          <dojo-question-validation-header>
            <div class="flex items-center gap-1">
              <dojo-icon-button [icon]="'arrow-left'" [iconSize]="18" (click)="setActiveQuestionSubItem(null)" />
              <span>{{ getSubItemEditTitle(subItem) }}</span>
            </div>
            <dojo-icon-button [icon]="'close'" [iconSize]="18" (click)="setActiveQuestionItem(null)" class="ml-auto" />
          </dojo-question-validation-header>

          <div class="question-wrap-content flex h-full flex-col">
            @if (subItem.value.type === ContentItemTypeEnum.Image) {
              @if (activeQuestionItem()?.type === 'markgroup') {
                <div class="flex h-full flex-col pb-2">
                  <dojo-question-image-editor
                    [contentJsonPath]="contentJsonPath()"
                    dojoMarkschemeImageBucket
                    [formCtrl]="$any(subItem)"
                    [fillInTheBlanksFormCtrl]="$any(subItem).controls.fill_in_the_blanks"
                    [editingOptions]="{
                      allowTextEditing: state.editingOptions.allowQuestionEditing,
                      allowImageEditing: state.editingOptions.allowImageEditing,
                      allowFillInTheBlanks: true,
                      hideImageAndTableMetadata: state.editingOptions.hideImageAndTableMetadata
                    }"
                  />
                </div>
              } @else {
                <div class="flex h-full flex-col pb-2">
                  <dojo-question-image-editor
                    [contentJsonPath]="contentJsonPath()"
                    [formCtrl]="$any(subItem)"
                    [fillInTheBlanksFormCtrl]="$any(subItem).controls.fill_in_the_blanks"
                    [editingOptions]="{
                      allowTextEditing: state.editingOptions.allowQuestionEditing,
                      allowImageEditing: state.editingOptions.allowImageEditing,
                      allowFillInTheBlanks: true,
                      hideImageAndTableMetadata: state.editingOptions.hideImageAndTableMetadata
                    }"
                  />
                </div>
              }
            } @else if (subItem.value.type === ContentItemTypeEnum.Table) {
              <div class="scrollbar-gutter-stable flex-1 overflow-y-auto overflow-x-hidden pb-2 pl-2">
                <dojo-question-table-editor
                  [formCtrl]="$any(subItem)"
                  [editingOptions]="{
                    allowTextEditing: state.editingOptions.allowQuestionEditing,
                    allowImageEditing: state.editingOptions.allowImageEditing,
                    allowFillInTheBlanks: true,
                    hideImageAndTableMetadata: state.editingOptions.hideImageAndTableMetadata
                  }"
                />
              </div>
            }
          </div>

          @if (
            state.editingOptions.allowQuestionStructureEditing ||
            (activeQuestionItem()?.type === 'markgroup' &&
              (state.editingOptions.allowMarkschemeStructureEditing ?? false))
          ) {
            <div class="sticky bottom-0 right-0 mt-auto border-t border-neutral-200 p-4">
              <dojo-button [customColor]="'red'" (click)="deleteContentItem()" [fill]="'outline'">
                Delete {{ subItem.value.type === ContentItemTypeEnum.Image ? 'image' : 'table' }}
              </dojo-button>
            </div>
          }
        </div>
      }

      @if (activeQuestionItem(); as activeItem) {
        <!-- TODO: header -->
        <div
          class="z-3 text-primary text-md sticky top-0 flex h-[45px] items-center justify-between border-b border-neutral-200 bg-neutral-50 px-4 py-3 font-semibold"
        >
          <span>Edit {{ CONTENT_ITEM_TYPE_TO_LONG_LABEL[activeItem.type] | lowercase }}</span>
          <dojo-icon-button [icon]="'close'" (click)="setActiveQuestionItem(null)" />
        </div>
        <div class="scrollbar-gutter-stable overflow-y-auto pb-3 pl-2 pt-3">
          @if (
            activeItem.type === QuestionItemTypeEnum.Stem ||
            activeItem.type === QuestionItemTypeEnum.Stimulus ||
            activeItem.type === QuestionItemTypeEnum.StemGroup
          ) {
            <dojo-content-item-editor
              [editingOptions]="state.editingOptions"
              [title]="CONTENT_ITEM_TYPE_TO_LONG_LABEL[activeItem.type] | titlecase"
              [contentItemsFormArray]="activeItem.ctrl.controls.content_items"
              [canAddRemoveText]="!!state.editingOptions.allowQuestionStructureEditing"
              [canAddRemoveImage]="!!state.editingOptions.allowQuestionStructureEditing"
              [canReorder]="!!state.editingOptions.allowQuestionStructureEditing"
              [supportsFillInTheBlanks]="activeItem.type === QuestionItemTypeEnum.Stem"
              (editItem)="editContentItem($event)"
              class="flex h-full flex-col px-2"
            />
          } @else if (activeItem.type === MarkschemeEditorItemEnum.MarkGroup) {
            <dojo-markscheme-markgroup-editor
              [editingOptions]="state.editingOptions"
              [formCtrl]="activeItem.ctrl"
              [solutionPathsCountFC]="activeItem.solutionPathsCountFC"
              (editItem)="editItem($event)"
              class="block px-2"
            />
          } @else if (activeItem.type === MarkschemeEditorItemEnum.WorkedSolution) {
            <dojo-content-item-editor
              [editingOptions]="state.editingOptions"
              [allowTextEditingOverride]="state.editingOptions.allowMarkschemeEditing"
              [title]="CONTENT_ITEM_TYPE_TO_LONG_LABEL[activeItem.type] | titlecase"
              [contentItemsFormArray]="activeItem.ctrl"
              [canAddRemoveText]="
                state.editingOptions.allowQuestionStructureEditing ||
                (state.editingOptions.allowMarkschemeStructureEditing ?? false)
              "
              [canAddRemoveImage]="
                state.editingOptions.allowQuestionStructureEditing ||
                (state.editingOptions.allowMarkschemeStructureEditing ?? false)
              "
              [canReorder]="
                state.editingOptions.allowQuestionStructureEditing ||
                (state.editingOptions.allowMarkschemeStructureEditing ?? false)
              "
              (editItem)="editWorkedSolutionItem($event)"
              class="flex h-full flex-col px-2"
              dojoMarkschemeImageBucket
            />
          } @else if (activeItem.type === QuestionItemTypeEnum.Options) {
            <dojo-question-item-options-editor
              [optionsFormGroup]="$any(activeItem.ctrl)"
              [editingOptions]="state.editingOptions"
              [allowQuestionStructureEditing]="!!state.editingOptions.allowQuestionStructureEditing"
              (editItem)="editItem($event)"
            />
          } @else {
            <div class="h-full w-full">TODO: {{ activeItem.type }}</div>
          }
        </div>
        @if (
          activeItem.type === QuestionItemTypeEnum.Stem ||
          activeItem.type === QuestionItemTypeEnum.Stimulus ||
          activeItem.type === QuestionItemTypeEnum.StemGroup ||
          activeItem.type === QuestionItemTypeEnum.Options ||
          activeItem.type === MarkschemeEditorItemEnum.MarkGroup
        ) {
          @if (
            state.editingOptions.allowQuestionStructureEditing ||
            (activeItem.type === MarkschemeEditorItemEnum.MarkGroup &&
              (state.editingOptions.allowMarkschemeStructureEditing ?? false)) ||
            (activeItem.type === QuestionItemTypeEnum.Options &&
              (state.editingOptions.allowMcqStructureEditing ?? false))
          ) {
            <div class="sticky bottom-0 right-0 mt-auto border-t border-neutral-200 p-4">
              <dojo-button [customColor]="'red'" (click)="deleteItem()" [fill]="'outline'">
                Delete {{ CONTENT_ITEM_TYPE_TO_LONG_LABEL[activeItem.type] | lowercase }}
              </dojo-button>
            </div>
          }
        }
      }
    }
  `,
  styles: [
    `
      .question-wrap-content {
        height: calc(100% - 45px);
      }
    `,
  ],
  host: { class: 'flex flex-col h-full w-full relative' },
  animations: [getAnimationSlideInToLeft()],
  imports: [
    ContentItemEditorComponent,
    MarkschemeMarkgroupEditorComponent,
    MarkschemeImageBucketDirective,
    QuestionImageEditorComponent,
    QuestionTableEditorComponent,
    IconButtonComponent,
    LowerCasePipe,
    ButtonComponent,
    TitleCasePipe,
    QuestionValidationHeaderComponent,
    QuestionItemOptionsEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionActiveItemEditorComponent {
  constructor(private readonly questionItemsFormService: QuestionItemsFormService) {}

  readonly QuestionItemTypeEnum = QuestionItemTypeEnum;
  readonly ContentItemTypeEnum = ContentItemTypeEnum;
  readonly CONTENT_ITEM_TYPE_TO_LONG_LABEL = CONTENT_ITEM_TYPE_TO_LONG_LABEL;
  readonly MarkschemeEditorItemEnum = MarkschemeEditorItemEnum;

  readonly activeQuestionSubItem = this.questionItemsFormService.activeQuestionSubItem;
  readonly activeQuestionSubItemCoordinates = this.questionItemsFormService.activeQuestionSubItemCoordinates;

  readonly activeQuestionItem = this.questionItemsFormService.activeQuestionItem;
  readonly activeQuestionItemCoordinates = this.questionItemsFormService.activeQuestionItemCoordinates;

  readonly contentJsonPath = this.questionItemsFormService.activeQuestionSubItemContentJsonPath;

  readonly editingOptions = this.questionItemsFormService.editingOptions$;

  readonly state = connectState({
    editingOptions: this.editingOptions,
  });

  setActiveQuestionSubItem(item: ImageContentItemFormGroup | TableContentItemFormGroup | null) {
    this.questionItemsFormService.setActiveQuestionSubItem(item);
  }

  setActiveQuestionItem(item: ActiveQuestionEditorItem | null) {
    this.questionItemsFormService.setActiveQuestionItem(item);
  }

  deleteItem() {
    const item = this.activeQuestionItem();

    if (!item) {
      return;
    }

    this.questionItemsFormService.deleteItem(item);
  }

  deleteContentItem() {
    const subItem = this.activeQuestionSubItem();
    const activeItem = this.activeQuestionItem();

    if (!subItem || !activeItem) {
      return;
    }

    // Find the content items form array based on the active item type
    let contentItemsFormArray: ContentItemFormArray | null = null;

    if (activeItem.type === 'markgroup') {
      // For markgroup, find content items in criterion
      contentItemsFormArray = (activeItem.ctrl as MarkGroupFormGroup).controls.criterion;
    } else if (
      activeItem.type === QuestionItemTypeEnum.Stem ||
      activeItem.type === QuestionItemTypeEnum.Stimulus ||
      activeItem.type === QuestionItemTypeEnum.StemGroup
    ) {
      // For other question items, content items are in content_items
      contentItemsFormArray = (activeItem.ctrl as StemFormGroup | StimulusFormGroup | StemGroupFormGroup).controls
        .content_items;
    } else if (activeItem.type === MarkschemeEditorItemEnum.WorkedSolution) {
      // For worked solution, the active item ctrl IS the content items form array
      contentItemsFormArray = activeItem.ctrl as ContentItemFormArray;
    }

    if (contentItemsFormArray) {
      const itemIndex = contentItemsFormArray.controls.findIndex((ctrl) => ctrl === subItem);
      if (itemIndex !== -1) {
        contentItemsFormArray.removeAt(itemIndex);
        contentItemsFormArray.markAsDirty();
        contentItemsFormArray.markAsTouched();
      }
    }

    // Close the edit panel
    this.setActiveQuestionSubItem(null);
  }

  editWorkedSolutionItem({ item, contentItemIndex }: { item: ContentItemFormGroup; contentItemIndex: number }) {
    this.editItem({ item, coordinates: { worked_solution: contentItemIndex } });
  }

  editContentItem({ item, contentItemIndex }: { item: ContentItemFormGroup; contentItemIndex: number }) {
    this.editItem({ item, coordinates: { content_items: contentItemIndex } });
  }

  editItem({ item, coordinates }: { item: ContentItemFormGroup; coordinates: ActiveQuestionEditorSubItemCoordinates }) {
    if (item.value.type === ContentItemTypeEnum.Image) {
      this.questionItemsFormService.setActiveQuestionSubItem(item as ImageContentItemFormGroup, coordinates);
    } else if (item.value.type === ContentItemTypeEnum.Table) {
      this.questionItemsFormService.setActiveQuestionSubItem(item as TableContentItemFormGroup, coordinates);
    }
  }

  getSubItemEditTitle(subItem: ImageContentItemFormGroup | TableContentItemFormGroup): string {
    if (subItem.value.type === ContentItemTypeEnum.Image) {
      return 'Edit image';
    } else if (subItem.value.type === ContentItemTypeEnum.Table) {
      return 'Edit table';
    }
    return 'Edit item';
  }
}
